import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, CheckCheck, CalendarCheck, MessageSquareWarning, UserPlus, TriangleAlert, ArrowRightLeft, Dot, Check, X } from "lucide-react";
import { Panel, Btn } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const ICONS = {
  booking: CalendarCheck, complaint: MessageSquareWarning, assignment: UserPlus,
  escalation: TriangleAlert, handover: ArrowRightLeft, info: Bell,
};
const TINT = {
  booking: "text-sky-300 bg-sky-500/15", complaint: "text-amber-300 bg-amber-500/15",
  assignment: "text-purple-300 bg-purple-500/15", escalation: "text-rose-300 bg-rose-500/15",
  handover: "text-emerald-300 bg-emerald-500/15", info: "text-purple-300 bg-purple-500/15",
};
const LABEL = {
  booking: "Reservation", complaint: "Guest request", assignment: "Assignment",
  escalation: "Escalation", handover: "Handover", info: "Update",
};

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export function NotificationsPanel({ title = "Notifications", intro, deptId, deptName }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = {};
    if (deptId) params.department_id = deptId;
    if (deptName) params.department_name = deptName;
    api.get("/notifications", { params })
      .then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [deptId, deptName]);

  const unread = items.filter((n) => !n.read).length;
  const markRead = async (n) => { if (!n.read) await api.post(`/notifications/${n.id}/read`); load(); };
  const markAll = async () => { await api.post("/notifications/read-all"); toast.success("All marked as read"); load(); };
  const open = (n) => { setSelected(n); markRead(n); };

  const canApprove = selected && selected.entity_type === "booking" && selected.entity_id && ["reception", "admin"].includes(user?.role);
  const decide = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/bookings/${selected.entity_id}/status`, { status });
      toast.success(`Reservation ${status === "approved" ? "approved" : "declined"}`);
      setSelected(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="notifications-panel">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Panel className="p-5"><div className="text-xs uppercase tracking-wider text-purple-300/60">Total</div><div className="font-head text-3xl font-extrabold text-white mt-1">{items.length}</div></Panel>
        <Panel className="p-5"><div className="text-xs uppercase tracking-wider text-purple-300/60">Unread</div><div className="font-head text-3xl font-extrabold text-[#FF6B7D] mt-1">{unread}</div></Panel>
        <Panel className="p-5 hidden sm:flex flex-col justify-center"><div className="text-xs uppercase tracking-wider text-purple-300/60">Feed</div><div className="text-sm text-purple-300 mt-1">Tap a notification for details.</div></Panel>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-head font-bold text-white flex items-center gap-2"><Bell size={18} /> {title}</h3>
        {unread > 0 && <Btn variant="outline" data-testid="notif-mark-all" onClick={markAll}><CheckCheck size={14} /> Mark all read</Btn>}
      </div>
      {intro && <p className="text-sm text-purple-300/70">{intro}</p>}

      {loading ? (
        <Panel className="p-10 text-center text-purple-400/70">Loading…</Panel>
      ) : !items.length ? (
        <Panel className="p-12 text-center text-purple-400/70">No notifications yet.</Panel>
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => open(n)}
                className={`w-full text-left flex items-start gap-3 rounded-xl border p-4 transition-colors ${n.read ? "bg-[#180735] border-[#2E1256] hover:border-[#451B7F]" : "bg-[#1E0A3E] border-[#FF2A6D]/30 hover:border-[#FF2A6D]/50"}`}>
                <span className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${TINT[n.type] || TINT.info}`}><Icon size={17} /></span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-1.5">
                    <span className="font-semibold text-white text-sm">{n.title}</span>
                    {!n.read && <Dot className="text-[#FF2A6D] -mx-1" size={22} />}
                  </span>
                  <span className="block text-sm text-purple-200/80 mt-0.5 truncate">{n.body}</span>
                  <span className="block text-[11px] text-purple-400 mt-1">{n.actor ? `${n.actor} · ` : ""}{timeAgo(n.created_at)}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#180735] border-[#2E1256] text-white" data-testid="notif-detail">
          {selected && (
            <>
              <DialogHeader>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-2 ${TINT[selected.type] || TINT.info}`}>
                  {(() => { const I = ICONS[selected.type] || Bell; return <I size={20} />; })()}
                </div>
                <DialogTitle className="text-white">{selected.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <span className="inline-block text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#2E1256] text-purple-200">{LABEL[selected.type] || "Update"}</span>
                <p className="text-sm text-purple-100/90 leading-relaxed">{selected.body}</p>
                <div className="text-xs text-purple-400 space-y-1 border-t border-[#2E1256] pt-3">
                  {selected.actor && <div>By <span className="text-purple-200">{selected.actor}</span></div>}
                  <div>{new Date(selected.created_at).toLocaleString()}</div>
                  {selected.entity_id && <div>Reference: <span className="text-purple-200">{selected.entity_id.slice(0, 8)}</span></div>}
                </div>
                {canApprove && (
                  <div className="flex gap-2 pt-1" data-testid="notif-approve-actions">
                    <Btn disabled={busy} data-testid="notif-approve" onClick={() => decide("approved")}><Check size={15} /> Approve</Btn>
                    <Btn variant="danger" disabled={busy} data-testid="notif-decline" onClick={() => decide("rejected")}><X size={15} /> Decline</Btn>
                  </div>
                )}
                {selected.link && !canApprove && (
                  <Btn variant="secondary" className="w-full" onClick={() => { window.location.href = selected.link; }}>Open related portal</Btn>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
